import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { CheckCircle2, Download, MessageSquareWarning, X } from "lucide-react";
import { toast } from "sonner";
import { publicApi, API } from "@/lib/api";
import { formatTime12 } from "@/lib/format";
import Logo from "@/components/Logo";
import StatusBadge from "@/components/StatusBadge";
import SignaturePad from "@/components/SignaturePad";
import { PayButton } from "@/components/PayButton";

function fmtDate(s) {
  if (!s) return "—";
  try {
    const d = new Date(s.length === 10 ? s + "T00:00:00" : s);
    return d.toLocaleDateString(undefined, { weekday: "long", year: "numeric", month: "long", day: "numeric" });
  } catch { return s; }
}

function money(n) {
  return `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export default function CustomerPortal() {
  const { token } = useParams();
  const [contract, setContract] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [signedByName, setSignedByName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [hasSig, setHasSig] = useState(false);
  const [showChange, setShowChange] = useState(false);
  const [changeMsg, setChangeMsg] = useState("");
  const [requesterName, setRequesterName] = useState("");
  const sigRef = useRef(null);
  const [lightbox, setLightbox] = useState(null);

  async function load() {
    setLoading(true);
    try {
      const { data } = await publicApi.get(`${API}/public/contracts/${token}`);
      setContract(data);
      setSignedByName((prev) => prev || data.client_name || "");
    } catch (e) {
      setError(e?.response?.data?.detail || "Contract not found");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, [token]);

  async function sign() {
    if (!sigRef.current || sigRef.current.isEmpty()) {
      toast.error("Please sign on the pad before submitting");
      return;
    }
    if (!signedByName.trim()) {
      toast.error("Please type your full name");
      return;
    }
    setSubmitting(true);
    try {
      const dataUrl = sigRef.current.toDataURL();
      await publicApi.post(`${API}/public/contracts/${token}/sign`, {
        signature_image: dataUrl,
        signed_by_name: signedByName.trim(),
      });
      toast.success("Contract signed");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to sign");
    } finally {
      setSubmitting(false);
    }
  }

  async function submitChange() {
    if (!changeMsg.trim()) {
      toast.error("Please describe the change");
      return;
    }
    try {
      await publicApi.post(`${API}/public/contracts/${token}/change-request`, {
        message: changeMsg.trim(),
        requester_name: requesterName.trim(),
      });
      toast.success("Change request sent");
      setShowChange(false);
      setChangeMsg("");
      load();
    } catch (e) {
      toast.error("Failed to send");
    }
  }

  async function downloadPdf() {
    try {
      const res = await fetch(`${API}/public/contracts/${token}/pdf`);
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `sheesh-contract-${(contract?.client_name || "client").replace(/\s+/g, "_")}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("Could not download PDF");
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] text-white flex items-center justify-center">
        <div className="text-white/40 text-sm uppercase tracking-widest">Loading contract…</div>
      </div>
    );
  }

  if (error || !contract) {
    return (
      <div className="min-h-screen bg-[#050505] text-white flex flex-col items-center justify-center px-4">
        <Logo size="md" />
        <div className="mt-8 text-white/60">{error}</div>
      </div>
    );
  }

  const services = [];
  if (contract.catering_food) services.push("Food Catering");
  if (contract.catering_mocktail) services.push("Mocktail Bar");
  if (contract.catering_hookah) services.push("Hookah Service");
  const isSigned = contract.status === "Signed";

  return (
    <div className="min-h-screen bg-[#050505] text-white relative" data-testid="customer-portal">
      <div
        className="absolute inset-x-0 top-0 h-72 opacity-30 bg-cover bg-center"
        style={{ backgroundImage: "url('https://images.pexels.com/photos/24863057/pexels-photo-24863057.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940')" }}
        aria-hidden
      />
      <div className="absolute inset-x-0 top-0 h-72 bg-gradient-to-b from-[#050505]/40 via-[#050505]/80 to-[#050505]" />

      <div className="relative max-w-4xl mx-auto px-4 sm:px-8 py-12 sm:py-16">
        <header className="text-center mb-10 fade-up">
          <Logo size="lg" />
          <div className="mt-3 text-[10px] uppercase tracking-[0.4em] text-white/50">Catering Service Agreement</div>
          <div className="mt-4 flex items-center justify-center gap-3">
            <StatusBadge status={contract.status} />
            {contract.viewed_at && (
              <span className="text-[10px] uppercase tracking-wider text-white/40">
                Viewed {new Date(contract.viewed_at).toLocaleDateString()}
              </span>
            )}
          </div>
        </header>

        {isSigned && (
          <div className="bg-[#C9963F]/10 border border-[#C9963F]/40 p-5 mb-6 flex items-start gap-3 fade-up" data-testid="signed-banner">
            <CheckCircle2 size={20} className="text-[#C9963F] mt-0.5" />
            <div className="flex-1">
              <div className="text-[#C9963F] font-medium">Contract Signed</div>
              <div className="text-white/70 text-sm mt-0.5">
                Signed by {contract.signed_by_name} on {new Date(contract.signed_at).toLocaleString()}.
              </div>
            </div>
            <button onClick={downloadPdf} data-testid="download-signed-pdf" className="inline-flex items-center gap-2 bg-[#C9963F] text-black px-4 py-2 text-sm font-medium hover:bg-[#A67A2D]">
              <Download size={14} /> Download PDF
            </button>
          </div>
        )}

        {/* Paper-like contract document */}
        <article className="bg-white text-[#0a0a0a] p-8 sm:p-12 shadow-2xl fade-up" data-testid="contract-document">
          <div className="flex items-start justify-between border-b-2 border-[#C9963F] pb-4 mb-6">
            <div>
              <div className="font-serif-brand text-2xl sm:text-3xl text-[#0a0a0a] italic">Sheesh Eatery & Lounge</div>
              <div className="text-xs text-gray-600 mt-1">4707 State Hwy 121, The Colony, TX 75056</div>
              <div className="text-xs text-gray-600">(214) 310-7650 · sheeshtx@gmail.com</div>
            </div>
            <div className="text-right">
              <div className="text-[10px] uppercase tracking-[0.2em] text-gray-500">Contract</div>
              <div className="text-xs text-gray-700 mt-1">{new Date().toLocaleDateString()}</div>
            </div>
          </div>

          <h1 className="font-serif-brand text-3xl mb-6">Catering Service Agreement</h1>

          <Section2 title="Client">
            <Pair label="Name" value={contract.client_name} />
            <Pair label="Phone" value={contract.client_phone || "—"} />
            <Pair label="Email" value={contract.client_email || "—"} />
          </Section2>

          <Section2 title="Event">
            <Pair label="Date" value={fmtDate(contract.event_date)} />
            <Pair label="Time" value={formatTime12(contract.event_time) || "—"} />
            <Pair label="Location" value={contract.event_location || "—"} full />
            <Pair label="Services" value={services.join(", ") || "—"} full />
          </Section2>

          <Section2 title="Menu & Package">
            <p className="whitespace-pre-wrap text-sm leading-relaxed">{contract.menu_description || "—"}</p>
          </Section2>

          {contract.addons && (
            <Section2 title="Add-ons / Service Type">
              <p className="whitespace-pre-wrap text-sm leading-relaxed">{contract.addons}</p>
            </Section2>
          )}

          {Array.isArray(contract.mockup_images) && contract.mockup_images.length > 0 && (
            <Section2 title="Mockup & Reference Images">
              <div className="sm:col-span-2">
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="customer-mockups">
                  {contract.mockup_images.map((img) => {
                    const src = `${API}/public/contracts/${token}/mockups/${img.id}`;
                    return (
                      <button
                        type="button"
                        key={img.id}
                        onClick={() => setLightbox(src)}
                        data-testid={`customer-mockup-${img.id}`}
                        className="relative aspect-square overflow-hidden border border-gray-200 hover:border-[#C9963F] transition-colors"
                      >
                        <img src={src} alt={img.filename} className="w-full h-full object-cover" />
                      </button>
                    );
                  })}
                </div>
              </div>
            </Section2>
          )}

          <Section2 title="Pricing">
            <div className="text-sm sm:col-span-2">
              <Row label="Guest Count" value={contract.guest_count} />
              {(contract.line_items && contract.line_items.length > 0) ? (
                <>
                  {contract.line_items.map((it, idx) => {
                    const up = Number(it.unit_price) || 0;
                    const qty = it.kind === "per_person" ? Number(contract.guest_count) || 0 : Number(it.quantity) || 0;
                    const line = qty * up;
                    const detail = it.kind === "per_person"
                      ? `${qty} × ${money(up)}/person`
                      : (qty !== 1 ? `${qty} × ${money(up)}` : "");
                    return (
                      <div key={it.id || idx} className="flex items-baseline justify-between py-1.5 border-b border-gray-100">
                        <div>
                          <div className="text-gray-900 text-sm">{it.label || (it.kind === "per_person" ? "Per Person" : "Item")}</div>
                          {detail && <div className="text-[10px] uppercase tracking-wider text-gray-500">{detail}</div>}
                        </div>
                        <span className="text-gray-900">{money(line)}</span>
                      </div>
                    );
                  })}
                  <Row label="Subtotal" value={money(contract.subtotal)} />
                </>
              ) : (
                <>
                  <Row label="Per Person" value={money(contract.per_person_price)} />
                  <Row label="Subtotal" value={money(contract.subtotal)} />
                  <Row label="Service Charge" value={money(contract.service_charge)} />
                </>
              )}
              <Row label="Tax" value={money(contract.tax)} />
              <div className="border-t-2 border-[#C9963F] mt-3 pt-3 flex items-baseline justify-between">
                <span className="text-xs uppercase tracking-widest text-gray-700">Total</span>
                <span className="font-serif-brand text-3xl text-[#0a0a0a]">{money(contract.total)}</span>
              </div>
            </div>
          </Section2>

          <Section2 title="Payment Schedule">
            <div className="sm:col-span-2 space-y-2 text-sm">
              {Array.isArray(contract.installments) && contract.installments.length > 0 ? (
                <>
                  {contract.installments.map((inst, idx) => (
                    <InstallmentRow
                      key={inst.id || idx}
                      inst={inst}
                      idx={idx}
                    />
                  ))}
                  <div className="flex items-baseline justify-between pt-3 border-t border-gray-200">
                    <span className="text-xs uppercase tracking-widest text-gray-700">Outstanding</span>
                    <span className={`font-serif-brand text-2xl ${Number(contract.amount_outstanding) === 0 ? "text-[#C9963F]" : "text-[#0a0a0a]"}`} data-testid="portal-outstanding">
                      {money(contract.amount_outstanding)}
                    </span>
                  </div>
                </>
              ) : (
                <>
                  <PaymentRow
                    label="50% Deposit"
                    sub="Due upon signing · non-refundable"
                    amount={contract.deposit_amount_computed}
                    paid={contract.deposit_paid}
                    paidInfo={contract.deposit_paid ? `${contract.deposit_method || ""}${contract.deposit_paid_at ? " · " + new Date(contract.deposit_paid_at).toLocaleDateString() : ""}` : ""}
                    testid="portal-deposit-row"
                  />
                  <PaymentRow
                    label="Remaining Balance"
                    sub={contract.balance_due_date_computed ? `Due by ${fmtDate(contract.balance_due_date_computed)}` : "Due 15 days before event"}
                    amount={contract.balance_amount_computed}
                    paid={contract.balance_paid}
                    paidInfo={contract.balance_paid ? `${contract.balance_method || ""}${contract.balance_paid_at ? " · " + new Date(contract.balance_paid_at).toLocaleDateString() : ""}` : ""}
                    testid="portal-balance-row"
                  />
                  <div className="flex items-baseline justify-between pt-3 border-t border-gray-200">
                    <span className="text-xs uppercase tracking-widest text-gray-700">Outstanding</span>
                    <span className={`font-serif-brand text-2xl ${Number(contract.amount_outstanding) === 0 ? "text-[#C9963F]" : "text-[#0a0a0a]"}`} data-testid="portal-outstanding">
                      {money(contract.amount_outstanding)}
                    </span>
                  </div>
                </>
              )}
            </div>
          </Section2>

          <Section2 title="Terms & Conditions">
            <pre className="whitespace-pre-wrap text-xs leading-relaxed font-sans text-gray-800">
              {contract.payment_terms}
            </pre>
          </Section2>

          {/* Signature on document */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-8 mt-10">
            <div>
              <div className="border-b-2 border-gray-800 h-20 flex items-end">
                {contract.signature_image && (
                  <img src={contract.signature_image} alt="signature" className="max-h-20" />
                )}
              </div>
              <div className="text-xs text-gray-600 mt-2">
                <div className="font-medium text-gray-800">{contract.signed_by_name || "Client Signature"}</div>
                <div>Date: {contract.signed_at ? new Date(contract.signed_at).toLocaleDateString() : "_______________"}</div>
              </div>
            </div>
            <div>
              <div className="border-b-2 border-gray-800 h-20 flex items-end pb-1">
                <span
                  className="font-serif-brand italic text-3xl text-gray-900"
                  style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
                  data-testid="rep-signature"
                >
                  Sheesh Eatery &amp; Lounge
                </span>
              </div>
              <div className="text-xs text-gray-600 mt-2">
                <div className="font-medium text-gray-800">Sheesh Eatery &amp; Lounge — Authorized Representative</div>
                <div>
                  Date:{" "}
                  {contract.created_at
                    ? new Date(contract.created_at).toLocaleDateString()
                    : new Date().toLocaleDateString()}
                </div>
              </div>
            </div>
          </div>
        </article>

        {/* Action: sign / change */}
        {!isSigned && contract.status !== "Cancelled" && (
          <div className="mt-8 bg-[#0e0e0e] border border-white/10 p-6 sm:p-8 fade-up" data-testid="signature-section">
            <h3 className="font-serif-brand text-2xl mb-2">Sign this contract</h3>
            <p className="text-white/50 text-sm mb-6">Sign with your finger on mobile or with your mouse on desktop.</p>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 items-end">
              <div className="sm:col-span-2">
                <SignaturePad ref={sigRef} onChange={(d) => setHasSig(!!d)} />
                <button
                  type="button"
                  onClick={() => sigRef.current?.clear()}
                  data-testid="clear-signature"
                  className="mt-2 text-xs text-white/50 hover:text-[#C9963F]"
                >
                  Clear signature
                </button>
              </div>
              <div>
                <label className="block text-[10px] uppercase tracking-[0.2em] text-white/50 mb-2">Type your full name</label>
                <input
                  value={signedByName}
                  onChange={(e) => setSignedByName(e.target.value)}
                  data-testid="input-signed-by-name"
                  className="w-full bg-transparent border-b border-white/20 px-0 py-2 focus:border-[#C9963F] focus:outline-none text-white"
                  placeholder="Full name"
                />
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-3 mt-6">
              <button
                onClick={sign}
                disabled={submitting}
                data-testid="submit-signature-btn"
                className="bg-[#C9963F] text-black font-medium px-6 py-3 hover:bg-[#A67A2D] transition-colors disabled:opacity-50"
              >
                {submitting ? "Submitting…" : "Agree & Sign"}
              </button>
              <button
                onClick={() => setShowChange(true)}
                data-testid="request-change-btn"
                className="border border-white/20 text-white/80 px-6 py-3 hover:border-[#C9963F] hover:text-[#C9963F] transition-colors inline-flex items-center justify-center gap-2"
              >
                <MessageSquareWarning size={14} /> Request a Change
              </button>
            </div>
          </div>
        )}

        {/* Change request modal */}
        {showChange && (
          <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setShowChange(false)}>
            <div className="bg-[#0e0e0e] border border-white/15 max-w-lg w-full p-6 sm:p-8 relative" onClick={(e) => e.stopPropagation()} data-testid="change-request-modal">
              <button onClick={() => setShowChange(false)} className="absolute top-4 right-4 text-white/50 hover:text-white" data-testid="close-change-modal">
                <X size={18} />
              </button>
              <h3 className="font-serif-brand text-2xl mb-2">Request a Change</h3>
              <p className="text-white/50 text-sm mb-5">Describe what you'd like changed. We'll review and update the contract.</p>
              <label className="block text-[10px] uppercase tracking-[0.2em] text-white/50 mb-2">Your name</label>
              <input
                value={requesterName}
                onChange={(e) => setRequesterName(e.target.value)}
                placeholder={contract.client_name}
                data-testid="input-requester-name"
                className="w-full bg-transparent border-b border-white/20 px-0 py-2 mb-4 focus:border-[#C9963F] focus:outline-none text-white"
              />
              <label className="block text-[10px] uppercase tracking-[0.2em] text-white/50 mb-2">Message</label>
              <textarea
                rows={5}
                value={changeMsg}
                onChange={(e) => setChangeMsg(e.target.value)}
                placeholder="e.g. Change guest count to 65, swap mocktails for tea service…"
                data-testid="input-change-message"
                className="w-full bg-[#0a0a0a] border border-white/10 px-3 py-2.5 focus:border-[#C9963F] focus:outline-none text-white"
              />
              <div className="flex justify-end gap-3 mt-5">
                <button onClick={() => setShowChange(false)} className="text-white/60 hover:text-white px-4 py-2 text-sm" data-testid="cancel-change-request">
                  Cancel
                </button>
                <button onClick={submitChange} className="bg-[#C9963F] text-black font-medium px-5 py-2 hover:bg-[#A67A2D] text-sm" data-testid="submit-change-request">
                  Send Request
                </button>
              </div>
            </div>
          </div>
        )}

        <footer className="text-center mt-12 text-[10px] uppercase tracking-[0.3em] text-white/30">
          Sheesh Eatery & Lounge · 4707 State Hwy 121 · The Colony, TX
        </footer>

        {lightbox && (
          <div
            className="fixed inset-0 bg-black/90 backdrop-blur-sm z-50 flex items-center justify-center p-6"
            onClick={() => setLightbox(null)}
            data-testid="customer-mockup-lightbox"
          >
            <button
              className="absolute top-5 right-5 text-white/70 hover:text-white"
              onClick={() => setLightbox(null)}
              data-testid="close-customer-lightbox"
            >
              <X size={24} />
            </button>
            <img src={lightbox} alt="mockup" className="max-w-full max-h-full object-contain" />
          </div>
        )}
      </div>
    </div>
  );
}

function Section2({ title, children }) {
  return (
    <section className="mb-7">
      <h2 className="text-[10px] uppercase tracking-[0.25em] text-[#C9963F] mb-3 border-b border-gray-200 pb-2">{title}</h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2">{children}</div>
    </section>
  );
}

function Pair({ label, value, full }) {
  return (
    <div className={full ? "sm:col-span-2" : ""}>
      <div className="text-[10px] uppercase tracking-wider text-gray-500">{label}</div>
      <div className="text-sm text-gray-900">{value}</div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-baseline justify-between py-1.5 border-b border-gray-100 last:border-b-0">
      <span className="text-gray-600 text-xs uppercase tracking-wider">{label}</span>
      <span className="text-gray-900">{value}</span>
    </div>
  );
}

function PaymentRow({ label, sub, amount, paid, paidInfo, testid }) {
  return (
    <div className="flex items-start justify-between py-2 border-b border-gray-100 last:border-b-0" data-testid={testid}>
      <div className="min-w-0">
        <div className="text-gray-900 font-medium">{label}</div>
        <div className="text-xs text-gray-500 mt-0.5">
          {paid ? (
            <span className="text-[#9a7228]">✓ PAID{paidInfo ? ` · ${paidInfo}` : ""}</span>
          ) : (
            sub
          )}
        </div>
      </div>
      <div className={`font-serif-brand text-xl ${paid ? "text-[#9a7228] line-through decoration-1" : "text-gray-900"}`}>
        ${Number(amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
      </div>
    </div>
  );
}

function InstallmentRow({ inst, idx }) {
  const paid = !!inst.paid;
  const link = (inst.payment_link || "").trim();
  const due = inst.due_date ? fmtDate(inst.due_date) : "";
  return (
    <div className="flex items-start justify-between gap-3 py-2 border-b border-gray-100 last:border-b-0" data-testid={`portal-installment-${idx}`}>
      <div className="min-w-0 flex-1">
        <div className="text-gray-900 font-medium">{inst.label || `Payment ${idx + 1}`}</div>
        <div className="text-xs text-gray-500 mt-0.5">
          {paid ? (
            <span className="text-[#9a7228]">
              ✓ PAID{inst.method ? ` · ${inst.method}` : ""}
              {inst.paid_at ? ` · ${new Date(inst.paid_at).toLocaleDateString()}` : ""}
            </span>
          ) : (
            <span>{due ? `Due ${due}` : "Due upon signing"}</span>
          )}
        </div>
        {!paid && link && (
          <PayButton link={link} label={inst.label || `Payment ${idx + 1}`} testid={`portal-pay-link-${idx}`} />
        )}
      </div>
      <div className={`font-serif-brand text-xl shrink-0 ${paid ? "text-[#9a7228] line-through decoration-1" : "text-gray-900"}`}>
        ${Number(inst.amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
      </div>
    </div>
  );
}
